<template>
    <b-modal id="addGroupNotesModal" modal-class="group-details-modal" centered size="md" hide-footer
        no-close-on-backdrop hide-header-close>
        <!-- Modal Header -->
        <template #modal-header>
            <div class="view-groups-header">
                <h5 class="modal-title text-center">Create Note</h5>
                <b-button variant="close-btn" class="btn-container" @click="closeModal">
                    <i class="fas fa-times"></i>
                </b-button>
            </div>
        </template>

        <b-container>
            <div class="form-part">
                <!-- NOTE INPUT -->
                <div class="mb-3">
                    <label for="noteInput">Notes <span class="text-danger">*</span></label>
                    <textarea id="noteInput" class="groupSearch-field w-100 mt-2" rows="4" v-model="note"
                        @input="noteError = ''" placeholder="Enter your note here..."></textarea>
                    <div v-if="noteError" class="form-error error-font-size mt-2">
                        {{ noteError }}
                    </div>
                </div>

                <div class="mb-3">
                    <label for="noteInput">Mark Note As <span class="text-danger">*</span></label>
                    <select id="noteInput" class="groupSearch-field mark-fields w-100 mt-2" v-model="status">
                        <option value="" selected>mark the note</option>
                        <option v-for="(item, key) in statusColors" :key="key" :value="item.value"
                            :style="{ color: item.color, fontWeight: 'bold' }">
                            {{ key }}
                        </option>
                    </select>
                    <div v-if="markError" class="form-error error-font-size mt-2">
                        {{ markError }}
                    </div>
                </div>

                <!-- FILE UPLOAD -->
                <div class="mb-3">
                    <label>Attachments <span class="text-muted">(optional)</span></label>
                    <div class="file-upload-container mt-2" :class="{ 'drag-over': isDragOver }"
                        @click="triggerFileInput" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave"
                        @drop.prevent="onDrop">
                        <i class="fas fa-upload upload-icon"></i>
                        <span>Click or drag & drop a file here</span>
                        <input type="file" ref="fileInput" class="d-none" @change="handleFile"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv" />
                    </div>
                    <div class="text-muted small mt-1">
                        Maximum file size: 4MB. Allowed types: pdf, doc, docx, xls, xlsx, csv.
                    </div>
                    <div v-if="fileName" class="mt-2 file-view-template">
                        <span>
                            {{ fileName }}
                        </span>
                        <i class="fa fa-trash excel-trash-icon" @click="removeFile"></i>
                    </div>
                </div>

                <!-- SAVE BUTTON -->
                <div class="notes-action-btns d-flex justify-content-end flex-column flex-md-row mt-4 gap-2">
                    <button class="group-univ-btn m-0" @click="saveNote" :disabled="isLoading">
                        {{ isLoading ? "Saving..." : "Submit" }}
                    </button>
                    <b-button variant="outline-secondary" @click="closeModal">Close</b-button>
                </div>
            </div>
        </b-container>
    </b-modal>
</template>

<script>
import Api from "@/resource/Api";

export default {
    name: "AddGroupNotesModal",
    props: {
        isOpen: Boolean,
        row: { type: Object, default: () => { } },
    },
    data() {
        return {
            note: "",
            noteError: "",
            file: null,
            fileName: "",
            isLoading: false,
            isDragOver: false,
            markError: "",
            status: "",
            statusColors: {
                Urgent: { color: "#FF0000", value: 1 },
                Important: { color: "#FF8000", value: 2 },
                High: { color: "#FFC300", value: 3 },
                Medium: { color: "#800000", value: 4 },
                Low: { color: "#0000FF", value: 5 },
                Information: { color: "#00A000", value: 6 },
                Special: { color: "#FF69B4", value: 8 }
            },
            nueraNoteUrl: process.env.VUE_APP_API_ADMIN,
            requestType: "group",
        };
    },
    watch: {
        isOpen(val) {
            if (val) this.$bvModal.show("addGroupNotesModal");
            this.status = "";
        },
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFile(e) {
            const fileContent = e.target.files[0];
            if (fileContent) {
                this.file = fileContent;
                this.fileName = fileContent.name;
            }
        },
        removeFile() {
            this.file = null;
            this.fileName = "";
        },
        onDragOver() {
            this.isDragOver = true;
        },
        onDragLeave() {
            this.isDragOver = false;
        },
        onDrop(event) {
            this.isDragOver = false;
            const fileContent = event.dataTransfer.files[0];
            if (fileContent) {
                this.file = fileContent;
                this.fileName = fileContent.name;
            }
        },
        resetState() {
            this.note = "";
            this.file = null;
            this.fileName = "";
            this.noteError = "";
            this.markError = "";
        },
        async saveNote() {
            if (!this.note.trim()) {
                this.noteError = "Note is required.";
                return;
            }

            if (!this.status) {
                this.markError = "The mark note as field is required";
                return;
            }

            let formData = new FormData();
            if (this.file) formData.append("file", this.file);
            formData.append("group_id", this.row.group_id);
            formData.append("note", this.note);
            formData.append("status", this.status);
            formData.append("request_type", this.requestType);

            this.isLoading = true;
            const loader = this.$loading.show();
            const url = 'v2/add-update-notes';
            Api.post(url, formData)
                .then(() => {
                    this.$notify("Note added successfully.");
                    this.$emit("noteAdded");
                    this.closeModal();
                })
                .catch((err) => {
                    this.$notify(err.response?.data?.message || "Failed to add note.", "error");
                })
                .finally(() => {
                    this.isLoading = false;
                    loader.hide();
                });
        },
        closeModal() {
            this.resetState();
            this.$bvModal.hide("addGroupNotesModal");
            this.$emit("close");
        },
    },
};
</script>

<style scoped>
.groupSearch-field{
    width: 100%;
    border: 1px solid #dce1e7;
    outline: none;
    border-radius: 6px;
    padding: 9px 15px;
    font-size: 14px;
    transition: all ease 0.6s;
}

.groupSearch-field:focus {
    border: 1px solid #03C3EC;
    transition: all ease 0.6s;
}

.form-part label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
}

.file-upload-container {
    border: 2px dashed #ccc;
    border-radius: 5px;
    padding: 1.25em;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.3s, background-color 0.3s;
}

.file-upload-container.drag-over {
    border-color: #0d6efd;
    background-color: #e7f1ff;
}

.upload-icon {
    font-size: 1.5rem
}

.file-view-template {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
}

.file-view-template span{
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.notes-action-btns button {
    min-width: 100px;
}

@media (max-width:576px) {
    .notes-action-btns button {
        width: 100%;
        max-width: none;
        margin-bottom: 0.5rem;
    }
}
</style>